import {
  PullQuoteEmbed as PullQuoteEmbedType,
  Gallery as GalleryEmbedType,
  SocialEmbed as SocialEmbedType,
  AccordionBlock as AccordionBlockType,
  TextBlock as TextBlockType,
  TableBlock as TableBlockType,
  Image,
  Video,
  Gallery,
  EmbedScriptEmbed,
  GamificationEntry as GamificationEntryType,
} from '@graphql/index';
import { ReactNode } from 'react';
import {
  SocialEmbed,
  UseClient,
  ScriptEmbed,
  AccordionBlock as BaseAccordionBlock,
  TextBlock as BaseTextBlock,
  RteTable,
  RteTableRow,
  RteTableCell,
  RteTableHeadCell,
} from '@stadionhq-v3/base-ui';
import {
  CleanContentful,
  CleanedContentfulImageProps,
  cleanContentful,
} from '@stadionhq-v3/clean-contentful';
import pullQuoteEmbedTransform from '@utils/transforms/components/organisms/pullQuoteEmbed.transform';
import galleryEmbedTransform from '@utils/transforms/components/organisms/galleryEmbed.transform';
import socialEmbedTransform from '@utils/transforms/components/organisms/socialEmbed.transform';
import imageEmbedTransform from '@utils/transforms/components/organisms/imageEmbed.transform';
import videoEmbedTransform from '@utils/transforms/components/organisms/videoEmbed.transform';
import scriptEmbedTransform from '@utils/transforms/components/organisms/scriptEmbed.transform';
import PullQuoteEmbed from '@components/v3/organisms/pull-quote-embed';
import TableBlock from '@components/v3/organisms/table-block';
import GalleryEmbed from '@components/v3/organisms/gallery-embed';
import VideoEmbed from '@components/v3/organisms/video-embed';
import ImageEmbed from '@components/v3/organisms/image-embed';
import { Plus } from '@components/icons';
import { THEMES, Theme, AccordionSection } from '@stadionhq-v3/core';
import { StreamSessionWrapper } from '@components/helpers/stream-session-wrapper';
import {
  RenderGamificationEntry,
  GamificationEntryJson,
} from '@components/helpers/content-block-renderer/blocks/gamification-entry';
import styles from '@components/v3/organisms/embed-block/embedBlock.module.scss';
import { EmbedDictionary } from './embedTypes';

export type EmbedEntry =
  | CleanContentful<PullQuoteEmbedType>
  | CleanContentful<GalleryEmbedType>
  | CleanContentful<SocialEmbedType>
  | CleanContentful<AccordionBlockType>
  | CleanContentful<TextBlockType>
  | CleanContentful<TableBlockType>
  | CleanContentful<Image>
  | CleanContentful<Video>
  | CleanContentful<GamificationEntryType>;

export type EntryHyperlink = {
  __typename: 'ArticlePage' | 'ContentPage';
  id: string;
  slug: string;
  title: string;
};

export type { EmbedDictionary };

const EMBED_TYPENAMES = {
  pullQuoteEmbed: 'PullQuoteEmbed',
  gallery: 'Gallery',
  socialEmbed: 'SocialEmbed',
  imageEmbed: 'Image',
  videoEmbed: 'Video',
  scriptEmbed: 'EmbedScriptEmbed',
  accordionBlock: 'AccordionBlock',
  textBlock: 'TextBlock',
  tableBlock: 'TableBlock',
  gamificationEntry: 'GamificationEntry',
};

// Helper to extract and render text from Contentful rich text JSON
// This is used for embedded accordions to avoid circular dependencies
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractTextFromRichText = (json: any): string => {
  if (!json?.content) return '';

  const renderNode = (node: {
    nodeType: string;
    value?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    content?: any[];
  }): string => {
    if (node.nodeType === 'text') {
      return node.value || '';
    }

    // Skip embedded entries - they'll be rendered separately
    if (node.nodeType === 'embedded-entry-block') {
      return '';
    }

    if (!node.content) return '';

    const innerContent = node.content.map(renderNode).join('');

    switch (node.nodeType) {
      case 'paragraph':
        return `<p>${innerContent}</p>`;
      case 'heading-2':
        return `<h2>${innerContent}</h2>`;
      case 'heading-3':
        return `<h3>${innerContent}</h3>`;
      case 'heading-4':
        return `<h4>${innerContent}</h4>`;
      case 'unordered-list':
        return `<ul>${innerContent}</ul>`;
      case 'ordered-list':
        return `<ol>${innerContent}</ol>`;
      case 'list-item':
        return `<li>${innerContent}</li>`;
      default:
        return innerContent;
    }
  };

  return json.content.map(renderNode).join('');
};

// Helper to render rich text JSON with styled React components
// Used for table blocks to get proper styling without circular dependencies
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderRichTextWithComponents = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  json: any,
  theme?: Theme,
  onDark?: boolean
): ReactNode => {
  if (!json?.content) return null;

  const renderNode = (
    node: {
      nodeType: string;
      value?: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      content?: any[];
    },
    index: number
  ): ReactNode => {
    if (node.nodeType === 'text') {
      return node.value || '';
    }

    // Skip embedded entries
    if (node.nodeType === 'embedded-entry-block') {
      return null;
    }

    const children = node.content?.map((child, i) => renderNode(child, i));

    switch (node.nodeType) {
      case 'paragraph':
        return <p key={index}>{children}</p>;
      case 'heading-2':
        return <h2 key={index}>{children}</h2>;
      case 'heading-3':
        return <h3 key={index}>{children}</h3>;
      case 'heading-4':
        return <h4 key={index}>{children}</h4>;
      case 'unordered-list':
        return <ul key={index}>{children}</ul>;
      case 'ordered-list':
        return <ol key={index}>{children}</ol>;
      case 'list-item':
        return <li key={index}>{children}</li>;
      case 'table':
        return (
          <RteTable
            key={`table-${index}`}
            theme={theme}
            onDark={onDark}
            nativeProps={{ style: { width: '100%' } }}
          >
            <tbody>{children}</tbody>
          </RteTable>
        );
      case 'table-row':
        return (
          <RteTableRow key={`row-${index}`} theme={theme} onDark={onDark}>
            {children}
          </RteTableRow>
        );
      case 'table-cell':
        return (
          <RteTableCell key={`cell-${index}`} theme={theme} onDark={onDark}>
            {children}
          </RteTableCell>
        );
      case 'table-header-cell':
        return (
          <RteTableHeadCell key={`head-${index}`} theme={theme} onDark={onDark}>
            {children}
          </RteTableHeadCell>
        );
      case 'document':
        return <>{children}</>;
      default:
        return children;
    }
  };

  return json.content.map((node: { nodeType: string }, i: number) =>
    renderNode(node, i)
  );
};

// Render embedded entries within accordion section content
const renderSectionEmbeddedEntries = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  links: any,
  onDark?: boolean,
  dictionary?: EmbedDictionary,
  packagePageLinkPath?: string
) => {
  if (!links?.entries?.block) return null;

  const embeddedBlocks = links.entries.block.filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (block: any) => block?.__typename
  );

  if (!embeddedBlocks.length) return null;

  return embeddedBlocks.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (block: any, index: number) => {
      const keyId = block.sys?.id || `embed-${index}`;
      switch (block.__typename) {
        case EMBED_TYPENAMES.textBlock: {
          const textBlock = cleanContentful(block)
            .all as CleanContentful<TextBlockType>;
          const theme = onDark ? THEMES.DEFAULT_2 : THEMES.DEFAULT_1;
          return (
            <BaseTextBlock
              key={textBlock.id || keyId}
              id={textBlock.id}
              title={textBlock.title}
              subTitle={textBlock.subTitle}
              introParagraph={textBlock.introParagraph}
              theme={(textBlock.theme as Theme) || theme}
              bodyCopy={
                textBlock.bodyCopy?.json ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: extractTextFromRichText(textBlock.bodyCopy.json),
                    }}
                  />
                ) : undefined
              }
            />
          );
        }
        case EMBED_TYPENAMES.imageEmbed:
          return (
            <ImageEmbed
              key={keyId}
              {...imageEmbedTransform(block as CleanedContentfulImageProps)}
            />
          );
        case EMBED_TYPENAMES.videoEmbed:
          return dictionary ? (
            <UseClient key={keyId}>
              <StreamSessionWrapper loadingLabel={dictionary.loading}>
                <VideoEmbed
                  {...videoEmbedTransform(block as CleanContentful<Video>)}
                  dictionary={dictionary}
                  packagePageLinkPath={packagePageLinkPath}
                />
              </StreamSessionWrapper>
            </UseClient>
          ) : null;
        case EMBED_TYPENAMES.gallery:
          return (
            block?.imagesCollection &&
            dictionary && (
              <GalleryEmbed
                key={keyId}
                {...galleryEmbedTransform(
                  cleanContentful(block).all as CleanContentful<Gallery> & {
                    images?: CleanedContentfulImageProps[];
                  },
                  { next: dictionary.next, previous: dictionary.previous }
                )}
                theme={onDark ? THEMES.DEFAULT_2 : THEMES.DEFAULT_1}
              />
            )
          );
        default:
          return null;
      }
    }
  );
};

export const renderDocumentEmbed = (
  entry: EmbedEntry,
  dictionary?: EmbedDictionary,
  onDark?: boolean,
  packagePageLinkPath?: string,
  compact?: boolean
) => {
  switch (entry.__typename) {
    case EMBED_TYPENAMES.pullQuoteEmbed:
      return (
        <PullQuoteEmbed
          {...pullQuoteEmbedTransform(
            entry as CleanContentful<PullQuoteEmbedType>
          )}
          theme={onDark ? THEMES.DEFAULT_2 : THEMES.DEFAULT_1}
          compact={compact}
        />
      );
    case EMBED_TYPENAMES.gallery:
      return (
        (entry as GalleryEmbedType)?.imagesCollection &&
        dictionary && (
          <>
            <GalleryEmbed
              {...galleryEmbedTransform(
                cleanContentful(entry).all as CleanContentful<Gallery> & {
                  images?: CleanedContentfulImageProps[];
                },
                { next: dictionary.next, previous: dictionary.previous }
              )}
              theme={onDark ? THEMES.DEFAULT_2 : THEMES.DEFAULT_1}
            />
          </>
        )
      );
    case EMBED_TYPENAMES.socialEmbed:
      return (
        <UseClient>
          <SocialEmbed
            {...socialEmbedTransform(entry as CleanContentful<SocialEmbedType>)}
          />
        </UseClient>
      );
    case EMBED_TYPENAMES.imageEmbed:
      return (
        <ImageEmbed
          {...imageEmbedTransform(entry as CleanedContentfulImageProps)}
        />
      );
    case EMBED_TYPENAMES.videoEmbed:
      return (
        dictionary && (
          <UseClient>
            <StreamSessionWrapper loadingLabel={dictionary.loading}>
              <VideoEmbed
                {...videoEmbedTransform(entry as CleanContentful<Video>)}
                dictionary={dictionary}
                packagePageLinkPath={packagePageLinkPath}
              />
            </StreamSessionWrapper>
          </UseClient>
        )
      );
    case EMBED_TYPENAMES.scriptEmbed:
      return (
        <UseClient>
          <div className={styles.scriptEmbedWrapper}>
            <ScriptEmbed
              {...scriptEmbedTransform(
                entry as CleanContentful<EmbedScriptEmbed>
              )}
            />
          </div>
        </UseClient>
      );
    case EMBED_TYPENAMES.gamificationEntry:
      return (
        <RenderGamificationEntry
          gamificationEntry={
            (entry as CleanContentful<GamificationEntryType>)
              .gamificationEntry as GamificationEntryJson | null
          }
        />
      );
    case EMBED_TYPENAMES.accordionBlock: {
      const accordionEntry = entry as CleanContentful<AccordionBlockType>;
      const accordionTheme = onDark ? THEMES.DEFAULT_2 : THEMES.DEFAULT_1;

      // Transform accordion sections - note: section content is not rendered
      // with ContentfulRichTextRenderer to avoid circular dependencies
      const sections: AccordionSection<ReactNode>[] =
        accordionEntry.accordionSections
          ?.filter((section) => section?.title)
          .map((section) => {
            const textContent = section?.content?.json
              ? extractTextFromRichText(section.content.json)
              : '';
            const embeddedEntries = renderSectionEmbeddedEntries(
              section?.content?.links,
              onDark,
              dictionary,
              packagePageLinkPath
            );

            return {
              id: section?.id || '',
              title: section?.title || '',
              assistiveText: section?.assistiveText,
              content: (
                <>
                  {textContent && (
                    <div dangerouslySetInnerHTML={{ __html: textContent }} />
                  )}
                  {embeddedEntries}
                </>
              ),
            };
          }) || [];

      return (
        <UseClient>
          <BaseAccordionBlock
            id={accordionEntry.id}
            title={accordionEntry.title}
            subtitle={accordionEntry.subtitle}
            introParagraph={accordionEntry.introParagraph}
            theme={(accordionEntry.theme as Theme) || accordionTheme}
            accordionSections={sections}
            icon={<Plus />}
          />
        </UseClient>
      );
    }
    case EMBED_TYPENAMES.textBlock: {
      const textBlockEntry = entry as CleanContentful<TextBlockType>;
      const textBlockTheme = onDark ? THEMES.DEFAULT_2 : THEMES.DEFAULT_1;
      return (
        <BaseTextBlock
          id={textBlockEntry.id}
          title={textBlockEntry.title}
          subTitle={textBlockEntry.subTitle}
          introParagraph={textBlockEntry.introParagraph}
          theme={(textBlockEntry.theme as Theme) || textBlockTheme}
          bodyCopy={
            textBlockEntry.bodyCopy?.json ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: extractTextFromRichText(textBlockEntry.bodyCopy.json),
                }}
              />
            ) : undefined
          }
        />
      );
    }
    case EMBED_TYPENAMES.tableBlock: {
      const tableBlockEntry = entry as CleanContentful<TableBlockType>;
      const tableBlockTheme =
        (tableBlockEntry.theme as Theme) ||
        (onDark ? THEMES.DEFAULT_2 : THEMES.DEFAULT_1);
      return (
        <TableBlock
          id={tableBlockEntry.id}
          title={tableBlockEntry.tableBlockTitle}
          subTitle={tableBlockEntry.introParagraph}
          theme={tableBlockTheme}
          bodyCopy={
            tableBlockEntry.tableAndBodyCopy?.json
              ? renderRichTextWithComponents(
                  tableBlockEntry.tableAndBodyCopy.json,
                  tableBlockTheme,
                  onDark
                )
              : undefined
          }
        />
      );
    }

    default:
      return <></>;
  }
};
